import React, { Component } from "react";
import {
  Row, Col, Card, Tag, Button, Divider, Empty,
  notification, Spin, Tabs, Avatar, Tooltip,
} from "antd";
import {
  UserOutlined, CheckCircleOutlined, ClockCircleOutlined,
  MailOutlined, ReloadOutlined, UserAddOutlined,
} from "@ant-design/icons";
import { noAuthlist, noAuthadd, add } from "../api/Apis";
import { withAppContext } from "../store/contextStore";
import Invitation from "./Invitation";

const { TabPane } = Tabs;

const NAVY  = "#1A2280";
const GOLD  = "#D4921A";
const GREEN = "#38A169";
const BORDER = "#E2E8F0";
const MUTED = "#718096";

const PROFILE_LABELS = {
  AG: { label: "Agent",  color: "blue"   },
  L:  { label: "Lender", color: "green"  },
  V:  { label: "Vendor", color: "purple" },
  B:  { label: "Buyer",  color: "orange" },
};

const PLAN_LABELS = {
  free:     { label: "Free",     color: "default" },
  member:   { label: "Member",   color: "blue"    },
  lifetime: { label: "Lifetime", color: "gold"    },
  agent:    { label: "Agent",    color: "green"   },
};

class MyNetwork extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      allUsers: [],
      invitedAgents:    [],
      invitedLenders:   [],
      invitedVendors:   [],
      invitedConsumers: [],
      inviteLinkCopied: false,
      reinviting: {},
      showInviteModal: false,
      inviteType: "agent",
    };
  }

  componentDidMount() {
    this.loadNetwork();
  }

  loadNetwork = async () => {
    const { userDetails } = this.props.context.state;
    if (!userDetails?.email) return;

    this.setState({ loading: true });
    try {
      const inviteHistory = userDetails.invitationHistory || [];

      // Fetch all users regardless — we need to check invitedBy too
      const result = await noAuthlist("getUsers");
      const allUsers = result.success ? (result.data || []) : [];

      // Build lookups
      const signedUpByEmail = {};
      const signedUpByInvitedBy = {}; // users who signed up via ref link
      allUsers.forEach(u => {
        if (u.email) signedUpByEmail[u.email.toLowerCase()] = u;
        // Track anyone whose invitedBy points to the current user
        if (u.invitedBy && u.invitedBy === userDetails.id) {
          signedUpByInvitedBy[u.email?.toLowerCase()] = u;
        }
      });

      // Build enriched list from invitationHistory
      const enriched = inviteHistory.map(inv => {
        // Check signup by email first, then by invitedBy field as fallback
        const signedUpUser =
          signedUpByEmail[inv.email?.toLowerCase()] ||
          // Also check if someone with that email has invitedBy set to us
          (signedUpByInvitedBy[inv.email?.toLowerCase()] || null);

        return {
          email:       inv.email,
          firstName:   inv.firstName || inv.email?.split("@")[0] || "",
          lastName:    inv.lastName  || "",
          userProfile: inv.type      || "AG",
          sentAt:      inv.sentAt,
          pending:     !signedUpUser,
          id:          signedUpUser?.id             || null,
          plan:        signedUpUser?.plan            || "free",
          profilePicture: signedUpUser?.profilePicture || null,
          subscriptionPack: signedUpUser?.subscriptionPack || null,
          signedUpAt:  signedUpUser?.createdAt       || null,
          // Extra confirmation: they explicitly credited this agent
          confirmedReferral: !!signedUpUser?.invitedBy && signedUpUser.invitedBy === userDetails.id,
        };
      });

      // Also add anyone who signed up via ref link but was NOT in invitationHistory
      // (e.g. agent shared link publicly — someone clicked and signed up without being pre-invited)
      const enrichedEmails = new Set(enriched.map(e => e.email?.toLowerCase()));
      Object.values(signedUpByInvitedBy).forEach(u => {
        if (!enrichedEmails.has(u.email?.toLowerCase())) {
          enriched.push({
            email:       u.email,
            firstName:   u.firstName || "",
            lastName:    u.lastName  || "",
            userProfile: u.userProfile || "AG",
            sentAt:      null, // no invite record — they found the link organically
            pending:     false,
            id:          u.id,
            plan:        u.plan || "free",
            profilePicture: u.profilePicture || null,
            subscriptionPack: u.subscriptionPack || null,
            signedUpAt:  u.createdAt || null,
            confirmedReferral: true,
            organicSignup: true, // signed up via ref link, not a pre-sent invite
          });
        }
      });

      // Sort: confirmed referrals first, then signed up, then pending; all by sentAt desc
      enriched.sort((a, b) => {
        if (a.confirmedReferral && !b.confirmedReferral) return -1;
        if (!a.confirmedReferral && b.confirmedReferral) return 1;
        if (!a.pending && b.pending) return -1;
        if (a.pending && !b.pending) return 1;
        return new Date(b.sentAt || 0) - new Date(a.sentAt || 0);
      });

      this.setState({
        allUsers,
        invitedAgents:    enriched.filter(u => u.userProfile === "AG"),
        invitedLenders:   enriched.filter(u => u.userProfile === "L"),
        invitedVendors:   enriched.filter(u => u.userProfile === "V"),
        invitedConsumers: enriched.filter(u => ["B","S","H","SB"].includes(u.userProfile)),
        loading: false,
      });
    } catch (e) {
      console.error("MyNetwork load error:", e);
      this.setState({ loading: false });
    }
  };

  handleReinvite = async (invitee) => {
    const { userDetails } = this.props.context.state;
    const key = invitee.email;
    this.setState(p => ({ reinviting: { ...p.reinviting, [key]: true } }));

    try {
      // Call the same invite endpoint as the Invitation component
      // Field names must match what the Lambda sendInvitation handler expects
      const data = {
        entityData: {
          firstName:  invitee.firstName || invitee.email,
          lastName:   invitee.lastName  || "",
          email:      invitee.email,
          agentName:  `${userDetails.firstName} ${userDetails.lastName}`.trim(),
          agentId:    userDetails.id,
          typeOfUser: invitee.type === "AG" ? "agent"
                    : invitee.type === "L"  ? "lender"
                    : invitee.type === "V"  ? "vendor"
                    : "agent",
        },
      };
      // Use add() (authenticated /api route) not noAuthadd() (/noAuth route which 502s)
      const result = await add("sendInvitation", data);
      if (result.success) {
        notification.success({
          message: "Invite sent!",
          description: `Re-invite sent to ${invitee.email}`,
        });
      } else {
        throw new Error(result.errors || "Failed to send");
      }
    } catch (e) {
      notification.error({ message: "Failed to send invite", description: e.message });
    }
    this.setState(p => ({ reinviting: { ...p.reinviting, [key]: false } }));
  };

  renderUserCard = (user) => {
    const profileType = PROFILE_LABELS[user.userProfile] || { label: user.userProfile, color: "default" };
    const planType    = PLAN_LABELS[user.plan] || { label: "Free", color: "default" };
    const hasPicture  = user.profilePicture?.[0];
    const photoUrl    = hasPicture
      ? `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/public/${user.profilePicture[0]}`
      : null;
    const isPending = user.pending;
    const reinviting = this.state.reinviting[user.email];

    return (
      <Card
        key={user.email}
        style={{
          marginBottom: "12px",
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
        bodyStyle={{ padding: "14px 18px" }}
      >
        <Row align="middle" gutter={[12, 0]}>
          {/* Avatar */}
          <Col style={{ flexShrink: 0 }}>
            <Avatar
              src={photoUrl}
              size={48}
              icon={<UserOutlined />}
              style={{ background: isPending ? "#f0f0f0" : NAVY, color: "white" }}
            >
              {!photoUrl && user.firstName?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Col>

          {/* Info */}
          <Col flex="1">
            <Row align="middle" style={{ gap: "8px", marginBottom: "2px" }}>
              <span style={{ fontWeight: 700, fontSize: "14px", color: NAVY }}>
                {isPending
                  ? user.email
                  : `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
              </span>
              <Tag color={profileType.color} style={{ margin: 0, borderRadius: "20px", fontSize: "11px" }}>
                {profileType.label}
              </Tag>
              {!isPending && (
                <Tag color={planType.color} style={{ margin: 0, borderRadius: "20px", fontSize: "11px" }}>
                  {planType.label}
                </Tag>
              )}
            </Row>
            <Row align="middle" style={{ gap: "8px" }}>
              {isPending ? (
                <>
                  <ClockCircleOutlined style={{ color: GOLD, fontSize: "12px" }} />
                  <span style={{ fontSize: "12px", color: MUTED }}>
                    Invite sent {user.sentAt ? new Date(user.sentAt).toLocaleDateString() : ""} — awaiting signup
                  </span>
                </>
              ) : (
                <>
                  <CheckCircleOutlined style={{ color: GREEN, fontSize: "12px" }} />
                  <span style={{ fontSize: "12px", color: GREEN, fontWeight: 600 }}>Account created</span>
                  {/* Confirmed referral badge — they credited this agent in their profile */}
                  {user.confirmedReferral && (
                    <Tag
                      style={{ margin: 0, borderRadius: "20px", fontSize: "10px",
                        background: "#fffbf0", border: `1px solid ${GOLD}`, color: GOLD, fontWeight: 700 }}>
                      ✓ Confirmed Referral
                    </Tag>
                  )}
                  {/* Organic signup — found the link themselves, not pre-invited */}
                  {user.organicSignup && (
                    <Tag style={{ margin: 0, borderRadius: "20px", fontSize: "10px",
                      background: "#f0f7f0", border: "1px solid #7A8F6E", color: "#7A8F6E", fontWeight: 600 }}>
                      Organic Signup
                    </Tag>
                  )}
                  {user.email && (
                    <span style={{ fontSize: "12px", color: MUTED, marginLeft: "8px" }}>
                      <MailOutlined style={{ marginRight: "3px" }} />{user.email}
                    </span>
                  )}
                  {user.signedUpAt && (
                    <span style={{ fontSize: "11px", color: MUTED, marginLeft: "8px" }}>
                      Joined {new Date(user.signedUpAt).toLocaleDateString()}
                    </span>
                  )}
                </>
              )}
            </Row>
          </Col>

          {/* Actions */}
          <Col style={{ flexShrink: 0 }}>
            <Tooltip title={isPending ? "Send invite again" : "Send another invite"}>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={reinviting}
                onClick={() => this.handleReinvite(user)}
                style={{
                  borderRadius: "50px",
                  borderColor: NAVY,
                  color: NAVY,
                  fontSize: "12px",
                }}
              >
                {isPending ? "Resend" : "Invite Again"}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Card>
    );
  };

  renderSection = (users, type) => {
    const { userDetails } = this.props.context.state;
    const inviterProfile = userDetails?.userProfile || "AG";
    const canInvite =
      userDetails?.userProfile === "AG" ||
      userDetails?.userProfile === "L"  ||
      userDetails?.userProfile === "V";

    // Map invite type to correct invitationTitle for Lambda routing
    const inviteTitleMap = {
      // Agent inviting anyone
      "AG_AG": "agent",
      "AG_L":  "Lendor",
      "AG_V":  "vendor",
      // Lender inviting
      "L_AG":  "lenderInvitesAgent",
      "L_L":   "lender",
      "L_V":   "vendor",
      // Vendor inviting
      "V_AG":  "vendorInvitesPro",
      "V_L":   "vendorInvitesPro",
      "V_V":   "vendor",
    };

    const typeLabels = {
      AG: { plural: "Agents",  inviteLabel: "Invite Agent",  inviteTitle: inviteTitleMap[inviterProfile + "_AG"] },
      L:  { plural: "Lenders", inviteLabel: "Invite Lender", inviteTitle: inviteTitleMap[inviterProfile + "_L"]  },
      V:  { plural: "Vendors", inviteLabel: "Invite Vendor", inviteTitle: inviteTitleMap[inviterProfile + "_V"]  },
    };

    const { plural, inviteLabel } = typeLabels[type] || { plural: type, inviteLabel: "Invite" };

    return (
      <div>
        <Row align="middle" justify="space-between" style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "15px", fontWeight: 700, color: NAVY }}>
            {users.length} {plural} in Your Network
          </div>
          {canInvite && (
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => this.setState({ showInviteModal: true, inviteType: type })}
              style={{
                borderRadius: "50px",
                background: NAVY,
                borderColor: NAVY,
                fontSize: "13px",
              }}
            >
              {inviteLabel}
            </Button>
          )}
        </Row>

        {users.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: MUTED }}>
                No {plural.toLowerCase()} in your network yet.
                {canInvite && " Use the button above to invite some!"}
              </span>
            }
          />
        ) : (
          users.map(u => this.renderUserCard(u))
        )}
      </div>
    );
  };

  render() {
    const { loading, invitedAgents, invitedLenders, invitedVendors, invitedConsumers, inviteLinkCopied, showInviteModal, inviteType } = this.state;
    const { userDetails } = this.props.context.state;
    const userProfile = userDetails?.userProfile || "AG";
    const inviterProfile = userProfile;

    const totalNetwork = invitedAgents.length + invitedLenders.length + invitedVendors.length + invitedConsumers.length;

    return (
      <div style={{ padding: "16px 0" }}>
        {/* Network growth headline */}
        <div style={{
          background: "linear-gradient(135deg, #0F1560 0%, #1A2280 100%)",
          borderRadius: "14px", padding: "20px 24px", marginBottom: "20px",
          borderLeft: "4px solid #D4921A",
        }}>
          <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "15px", marginBottom: "8px" }}>
            🌱 Build Your Network. Earn More Together.
          </div>
          <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "13px", lineHeight: 1.7 }}>
            Use your personal invitation links to build and track your network. The more you grow your network of professionals, the more incentives you will earn — including potential future stock benefits, free upgrades, revenue share, and more. Let's grow this together and build a brighter future for everyone!
          </div>
        </div>

        {/* Summary bar */}
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          {[
            { label: "Total Network", value: totalNetwork,              color: NAVY },
            { label: "Agents",        value: invitedAgents.length,     color: "#1890ff" },
            { label: "Lenders",       value: invitedLenders.length,    color: GREEN },
            { label: "Vendors",       value: invitedVendors.length,    color: "#722ed1" },
            { label: "Consumers",     value: invitedConsumers.length,  color: GOLD },
          ].map(({ label, value, color }) => (
            <Col key={label} xs={12} sm={12} md={6} lg={4}>
              <Card
                style={{ borderRadius: "12px", border: `1px solid ${BORDER}`, textAlign: "center" }}
                bodyStyle={{ padding: "14px" }}
              >
                <div style={{ fontSize: "24px", fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>{label}</div>
              </Card>
            </Col>
          ))}
        </Row>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ color: MUTED, marginTop: "12px" }}>Loading your network...</div>
          </div>
        ) : (
          <Tabs
            defaultActiveKey={userProfile === "V" ? "V" : "AG"}
            tabBarStyle={{ fontWeight: 600, color: NAVY }}
          >
            {/* Agents tab — shown to AG, L, V */}
            <TabPane
              tab={<span>👤 Agents ({invitedAgents.length})</span>}
              key="AG"
            >
              {this.renderSection(invitedAgents, "AG")}
            </TabPane>
            {/* Lenders tab — shown to AG, L, V */}
            <TabPane
              tab={<span>🏦 Lenders ({invitedLenders.length})</span>}
              key="L"
            >
              {this.renderSection(invitedLenders, "L")}
            </TabPane>
            {/* Vendors tab — shown to all */}
            <TabPane
              tab={<span>🛍️ Vendors ({invitedVendors.length})</span>}
              key="V"
            >
              {this.renderSection(invitedVendors, "V")}
            </TabPane>

            {/* Consumers tab — buyers, sellers, homeowners */}
            <TabPane
              tab={<span>🏡 Consumers ({invitedConsumers.length})</span>}
              key="consumers"
            >
              {this.renderSection(invitedConsumers, "B")}
            </TabPane>

            {/* Invite Link tab */}
            <TabPane
              tab={<span>🔗 Invite Link</span>}
              key="invite_link"
            >
              <div style={{ padding: "8px 0" }}>
                {/* ── Quick invite link ───────────────────────────── */}
                <div style={{
                  background: "linear-gradient(135deg, #0F1560 0%, #1A2280 100%)",
                  borderRadius: "14px", padding: "24px", marginBottom: "16px",
                }}>
                  <div style={{ color: "#FFFFFF", fontWeight: 800, fontSize: "16px", marginBottom: "6px" }}>
                    🔗 Your Personal Invite Link
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "13px", marginBottom: "14px", lineHeight: 1.5 }}>
                    Share this link anywhere. Anyone who signs up gets added to your network automatically — no manual entry needed.
                  </div>

                  {/* Client / Consumer link */}
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ color: "#7EC8E3", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>
                      🏡 For Clients & Consumers
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input readOnly id="mnClientLink"
                        value={"https://www.myhomebuddha.com/register?ref=" + (userDetails?.id || "") + "&type=B&agentName=" + encodeURIComponent((userDetails?.firstName || "") + " " + (userDetails?.lastName || ""))}
                        style={{ flex: 1, padding: "9px 12px", borderRadius: "8px", border: "none", fontSize: "12px", background: "rgba(255,255,255,0.92)", color: "#111", minWidth: 0 }}
                      />
                      <Button size="small" type="primary"
                        style={{ background: GOLD, border: "none", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}
                        onClick={() => {
                          document.getElementById("mnClientLink").select();
                          document.execCommand("copy");
                          this.setState({ inviteLinkCopied: "client" });
                          setTimeout(() => this.setState({ inviteLinkCopied: false }), 2500);
                        }}>
                        {inviteLinkCopied === "client" ? "✓ Copied!" : "📋 Copy"}
                      </Button>
                    </div>
                  </div>

                  {/* Agent link */}
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ color: "#A5B4FC", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>
                      🤝 For Fellow Agents
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input readOnly id="mnAgentLink"
                        value={"https://www.myhomebuddha.com/register?ref=" + (userDetails?.id || "") + "&type=AG&agentName=" + encodeURIComponent((userDetails?.firstName || "") + " " + (userDetails?.lastName || ""))}
                        style={{ flex: 1, padding: "9px 12px", borderRadius: "8px", border: "none", fontSize: "12px", background: "rgba(255,255,255,0.92)", color: "#111", minWidth: 0 }}
                      />
                      <Button size="small" type="primary"
                        style={{ background: GOLD, border: "none", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}
                        onClick={() => {
                          document.getElementById("mnAgentLink").select();
                          document.execCommand("copy");
                          this.setState({ inviteLinkCopied: "agent" });
                          setTimeout(() => this.setState({ inviteLinkCopied: false }), 2500);
                        }}>
                        {inviteLinkCopied === "agent" ? "✓ Copied!" : "📋 Copy"}
                      </Button>
                    </div>
                  </div>

                  {/* Lender link */}
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ color: "#86EFAC", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>
                      🏦 For Lenders
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input readOnly id="mnLenderLink"
                        value={"https://www.myhomebuddha.com/register?ref=" + (userDetails?.id || "") + "&type=L&agentName=" + encodeURIComponent((userDetails?.firstName || "") + " " + (userDetails?.lastName || ""))}
                        style={{ flex: 1, padding: "9px 12px", borderRadius: "8px", border: "none", fontSize: "12px", background: "rgba(255,255,255,0.92)", color: "#111", minWidth: 0 }}
                      />
                      <Button size="small" type="primary"
                        style={{ background: GOLD, border: "none", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}
                        onClick={() => {
                          document.getElementById("mnLenderLink").select();
                          document.execCommand("copy");
                          this.setState({ inviteLinkCopied: "lender" });
                          setTimeout(() => this.setState({ inviteLinkCopied: false }), 2500);
                        }}>
                        {inviteLinkCopied === "lender" ? "✓ Copied!" : "📋 Copy"}
                      </Button>
                    </div>
                  </div>

                  {/* Vendor link */}
                  <div>
                    <div style={{ color: "#FCD34D", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>
                      🔧 For Vendors & Service Providers
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input readOnly id="mnVendorLink"
                        value={"https://www.myhomebuddha.com/register?ref=" + (userDetails?.id || "") + "&type=V&agentName=" + encodeURIComponent((userDetails?.firstName || "") + " " + (userDetails?.lastName || ""))}
                        style={{ flex: 1, padding: "9px 12px", borderRadius: "8px", border: "none", fontSize: "12px", background: "rgba(255,255,255,0.92)", color: "#111", minWidth: 0 }}
                      />
                      <Button size="small" type="primary"
                        style={{ background: GOLD, border: "none", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}
                        onClick={() => {
                          document.getElementById("mnVendorLink").select();
                          document.execCommand("copy");
                          this.setState({ inviteLinkCopied: "vendor" });
                          setTimeout(() => this.setState({ inviteLinkCopied: false }), 2500);
                        }}>
                        {inviteLinkCopied === "vendor" ? "✓ Copied!" : "📋 Copy"}
                      </Button>
                    </div>
                  </div>

                  <div style={{ color: "rgba(255,255,255,0.38)", fontSize: "10px", marginTop: "14px" }}>
                    Signups via any link appear in the correct network tab automatically
                  </div>
                </div>

                {/* Email invite buttons */}
                <div style={{ background: "white", borderRadius: "12px", border: "1px solid " + BORDER, padding: "20px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: NAVY, marginBottom: "4px" }}>
                    ✉️ Invite by Email
                  </div>
                  <div style={{ fontSize: "12px", color: MUTED, marginBottom: "14px" }}>
                    Send a personalised invitation email directly to someone.
                  </div>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {[
                      { type: "AG", label: "Invite Agent",   icon: "👤" },
                      { type: "L",  label: "Invite Lender",  icon: "🏦" },
                      { type: "V",  label: "Invite Vendor",  icon: "🔧" },
                      { type: "B",  label: "Invite Client",  icon: "🏡" },
                    ].map(({ type, label, icon }) => (
                      <Button key={type}
                        icon={<MailOutlined />}
                        onClick={() => this.setState({ showInviteModal: true, inviteType: type })}
                        style={{ borderRadius: "50px", borderColor: NAVY, color: NAVY, fontWeight: 600 }}>
                        {icon} {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </TabPane>

          </Tabs>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <Invitation
            enableInvitationModal={showInviteModal}
            handleCloseInvitationModal={() => {
              this.setState({ showInviteModal: false });
              this.loadNetwork();
            }}
            invitationTitle={
              inviteType === "AG" ? (inviterProfile === "L" ? "lenderInvitesAgent" : inviterProfile === "V" ? "vendorInvitesPro" : "agent") :
              inviteType === "L"  ? (inviterProfile === "L" ? "lender" : inviterProfile === "V" ? "vendorInvitesPro" : "Lendor") :
              "vendor"
            }
          />
        )}
      </div>
    );
  }
}

export default withAppContext(MyNetwork);